import {
  FIXED_PHYSICS_STEP,
  TrackBoundDynamics,
  createTrackBoundState,
  type CoasterFamilyDefinition,
  type GVector,
  type SimulationFrame,
  type TrackBoundState,
  type ValidationIssue,
} from '../physics/index.ts';
import type { SemanticSectionType, TrackSampler } from '../track/index.ts';

export type RideOutcome =
  'running' | 'completed' | 'stalled' | 'arrival_failed' | 'timed_out' | 'blocked' | 'ended';
export type RideEventKind =
  | 'section_enter'
  | 'station_dispatch'
  | 'station_arrival_failed'
  | 'photo'
  | 'warning'
  | 'stall'
  | 'complete';

export interface RideEvent {
  readonly kind: RideEventKind;
  readonly time: number;
  readonly distance: number;
  readonly sectionId?: string;
  readonly sectionType?: SemanticSectionType;
  readonly code?: string;
}

export interface RideTraceSample {
  readonly time: number;
  readonly distance: number;
  readonly speed: number;
  readonly direction: -1 | 1;
  readonly verticalG: number;
  readonly lateralG: number;
  readonly longitudinalG: number;
  readonly sectionType: SemanticSectionType | null;
}

export interface RidePeak {
  readonly value: number;
  readonly time: number;
  readonly distance: number;
  readonly carIndex: number;
}

export interface RideSummary {
  readonly outcome: Exclude<RideOutcome, 'running'>;
  readonly durationSeconds: number;
  readonly progressPercent: number;
  readonly distanceTravelled: number;
  readonly topSpeed: RidePeak;
  readonly maxVerticalG: RidePeak;
  readonly minVerticalG: RidePeak;
  readonly maxAbsLateralG: RidePeak;
  readonly maxAbsLongitudinalG: RidePeak;
  readonly issues: readonly ValidationIssue[];
  readonly events: readonly RideEvent[];
  readonly trace: readonly RideTraceSample[];
}

export interface RideRunnerConfig {
  readonly sampler: TrackSampler;
  readonly family: CoasterFamilyDefinition;
  readonly initialDistance: number;
  readonly initialSpeed?: number;
  readonly maxDurationSeconds?: number;
  readonly stallGraceSeconds?: number;
  readonly stallDurationSeconds?: number;
  readonly stallSpeedMps?: number;
  readonly traceHz?: number;
  readonly stationArrivalToleranceMps?: number;
}

const emptyPeak = (value: number): RidePeak => ({ value, time: 0, distance: 0, carIndex: 0 });

function forceVector(frame: SimulationFrame): GVector {
  const force = frame.forces[0];
  return force?.windows['200'] ?? force ?? { vertical: 1, lateral: 0, longitudinal: 0 };
}

export class RideRunner {
  private readonly backend = new TrackBoundDynamics();
  private state: TrackBoundState;
  private previousFrame: SimulationFrame | null = null;
  private currentFrame: SimulationFrame | null = null;
  private outcome: RideOutcome = 'running';
  private travelled = 0;
  private netTravelled = 0;
  private stalledFor = 0;
  private previousSectionId: string | null = null;
  private stationReleased = false;
  private stationArrivalStarted = false;
  private stationArrivalEntrySpeed = 0;
  private stationArrivalSectionId: string | null = null;
  private traceAccumulator = 0;
  private readonly trace: RideTraceSample[] = [];
  private readonly events: RideEvent[] = [];
  private readonly issues: ValidationIssue[] = [];
  private topSpeed = emptyPeak(0);
  private maxVerticalG = emptyPeak(1);
  private minVerticalG = emptyPeak(1);
  private maxAbsLateralG = emptyPeak(0);
  private maxAbsLongitudinalG = emptyPeak(0);

  constructor(private readonly config: RideRunnerConfig) {
    this.state = createTrackBoundState(config.initialDistance, config.initialSpeed ?? 0);
  }

  get result(): RideOutcome {
    return this.outcome;
  }

  get referenceState(): TrackBoundState {
    return this.state;
  }

  get frame(): SimulationFrame | null {
    return this.currentFrame;
  }

  get previous(): SimulationFrame | null {
    return this.previousFrame;
  }

  get progressPercent(): number {
    if (this.outcome === 'completed') return 100;
    return Math.max(0, Math.min(100, (this.travelled / this.config.sampler.length) * 100));
  }

  step(dt = FIXED_PHYSICS_STEP): SimulationFrame {
    if (this.outcome !== 'running' && this.currentFrame) return this.currentFrame;
    const before = this.state;
    const frame = this.backend.step(
      before,
      { sampler: this.config.sampler, family: this.config.family },
      dt,
    );
    this.previousFrame = this.currentFrame;
    this.currentFrame = frame;
    this.state = frame.referenceState;
    this.consumeStep(before, frame, dt);
    return frame;
  }

  advance(renderDeltaSeconds: number, accumulator = 0): { accumulator: number; steps: number } {
    let remaining = Math.max(0, accumulator + Math.min(0.25, renderDeltaSeconds));
    let steps = 0;
    while (remaining + 1e-12 >= FIXED_PHYSICS_STEP && this.outcome === 'running') {
      this.step();
      remaining -= FIXED_PHYSICS_STEP;
      steps += 1;
    }
    return { accumulator: Math.max(0, remaining), steps };
  }

  stop(): void {
    if (this.outcome === 'running') this.outcome = 'ended';
  }

  eventsSince(index: number): readonly RideEvent[] {
    return this.events.slice(Math.max(0, index));
  }

  get eventCount(): number {
    return this.events.length;
  }

  summary(): RideSummary {
    const outcome = this.outcome === 'running' ? 'ended' : this.outcome;
    return {
      outcome,
      durationSeconds: this.state.time,
      progressPercent: this.progressPercent,
      distanceTravelled: this.travelled,
      topSpeed: this.topSpeed,
      maxVerticalG: this.maxVerticalG,
      minVerticalG: this.minVerticalG,
      maxAbsLateralG: this.maxAbsLateralG,
      maxAbsLongitudinalG: this.maxAbsLongitudinalG,
      issues: [...this.issues],
      events: [...this.events],
      trace: [...this.trace],
    };
  }

  private consumeStep(before: TrackBoundState, frame: SimulationFrame, dt: number): void {
    if (frame.issues.some((issue) => issue.severity === 'block')) {
      this.issues.push(...frame.issues);
      this.outcome = 'blocked';
      return;
    }
    this.issues.push(...frame.issues);
    let delta = frame.referenceState.sRef - before.sRef;
    const length = this.config.sampler.length;
    if (frame.referenceState.direction === 1 && delta < -length / 2) delta += length;
    if (frame.referenceState.direction === -1 && delta > length / 2) delta -= length;
    this.netTravelled += delta;
    this.travelled = Math.max(this.travelled, this.netTravelled);

    const sample = this.config.sampler.sampleByDistance(frame.referenceState.sRef);
    const section = sample.section;
    if (section?.id !== this.previousSectionId) {
      this.previousSectionId = section?.id ?? null;
      if (section)
        this.events.push({
          kind: 'section_enter',
          time: frame.time,
          distance: frame.referenceState.sRef,
          sectionId: section.id,
          sectionType: section.type,
        });
      if (section?.type === 'photo')
        this.events.push({
          kind: 'photo',
          time: frame.time,
          distance: frame.referenceState.sRef,
          sectionId: section.id,
          sectionType: section.type,
        });
    }
    const station = frame.referenceState.sectionRuntime.station as
      { released?: unknown } | undefined;
    if (!this.stationReleased && station?.released === true) {
      this.stationReleased = true;
      this.events.push({
        kind: 'station_dispatch',
        time: frame.time,
        distance: frame.referenceState.sRef,
        ...(section ? { sectionId: section.id, sectionType: section.type } : {}),
      });
    }

    this.capturePeaks(frame);
    this.traceAccumulator += dt;
    const traceStep = 1 / Math.max(1, this.config.traceHz ?? 30);
    if (this.traceAccumulator + 1e-12 >= traceStep) {
      this.traceAccumulator %= traceStep;
      const vector = forceVector(frame);
      this.trace.push({
        time: frame.time,
        distance: frame.referenceState.sRef,
        speed: frame.referenceState.velocity,
        direction: frame.referenceState.direction,
        verticalG: vector.vertical,
        lateralG: vector.lateral,
        longitudinalG: vector.longitudinal,
        sectionType: section?.type ?? null,
      });
    }

    const stationHeld = section?.type === 'station' && station?.released === false;
    this.stalledFor =
      frame.referenceState.velocity < (this.config.stallSpeedMps ?? 0.45) && !stationHeld
        ? this.stalledFor + dt
        : 0;
    const grace = this.config.stallGraceSeconds ?? 4;
    const stallDuration = this.config.stallDurationSeconds ?? 2;
    const returningToStation =
      this.stationReleased && section?.type === 'station' && this.netTravelled >= length * 0.9;
    if (returningToStation && !this.stationArrivalStarted) {
      this.stationArrivalStarted = true;
      this.stationArrivalEntrySpeed = frame.referenceState.velocity;
      this.stationArrivalSectionId = section.id;
    }
    const stationTargetSpeed =
      section?.type === 'station' && typeof section.parameters?.targetSpeed === 'number'
        ? Math.max(0, section.parameters.targetSpeed)
        : 0;
    const arrivalSpeedLimit = stationTargetSpeed + (this.config.stationArrivalToleranceMps ?? 0.25);
    const allCarsInStation =
      returningToStation &&
      frame.cars.length > 0 &&
      frame.cars.every(
        (car) => this.config.sampler.sampleByDistance(car.distance).section?.type === 'station',
      );
    const completed = allCarsInStation && frame.referenceState.velocity <= arrivalSpeedLimit;
    const leftArrivalStation =
      this.stationArrivalStarted &&
      section?.type !== 'station' &&
      this.netTravelled >= length * 0.95;
    if (completed) {
      this.outcome = 'completed';
      this.events.push({ kind: 'complete', time: frame.time, distance: frame.referenceState.sRef });
    } else if (leftArrivalStation) {
      this.outcome = 'arrival_failed';
      this.issues.push({
        code: 'station_arrival_overspeed',
        severity: 'block',
        messageKey: 'The train passes through the station too fast to finish the ride.',
        sectionId: this.stationArrivalSectionId ?? undefined,
        distance: frame.referenceState.sRef,
        measured: this.stationArrivalEntrySpeed,
        threshold: arrivalSpeedLimit,
      });
      this.events.push({
        kind: 'station_arrival_failed',
        time: frame.time,
        distance: frame.referenceState.sRef,
        sectionId: this.stationArrivalSectionId ?? undefined,
        sectionType: 'station',
      });
    } else if (frame.time >= grace && this.stalledFor >= stallDuration) {
      this.outcome = 'stalled';
      this.events.push({ kind: 'stall', time: frame.time, distance: frame.referenceState.sRef });
    } else if (frame.time >= (this.config.maxDurationSeconds ?? 180)) {
      this.outcome = 'timed_out';
    }
  }

  private capturePeaks(frame: SimulationFrame): void {
    if (frame.referenceState.velocity > this.topSpeed.value)
      this.topSpeed = {
        value: frame.referenceState.velocity,
        time: frame.time,
        distance: frame.referenceState.sRef,
        carIndex: 0,
      };
    frame.forces.forEach((force) => {
      const windowed = force.windows['200'] ?? force;
      const make = (value: number): RidePeak => ({
        value,
        time: frame.time,
        distance: force.distance,
        carIndex: force.carIndex,
      });
      if (windowed.vertical > this.maxVerticalG.value) this.maxVerticalG = make(windowed.vertical);
      if (windowed.vertical < this.minVerticalG.value) this.minVerticalG = make(windowed.vertical);
      if (Math.abs(windowed.lateral) > Math.abs(this.maxAbsLateralG.value))
        this.maxAbsLateralG = make(windowed.lateral);
      if (Math.abs(windowed.longitudinal) > Math.abs(this.maxAbsLongitudinalG.value))
        this.maxAbsLongitudinalG = make(windowed.longitudinal);
    });
  }
}

export function runRideToCompletion(config: RideRunnerConfig): RideSummary {
  const runner = new RideRunner(config);
  const maxSteps = Math.ceil((config.maxDurationSeconds ?? 180) / FIXED_PHYSICS_STEP) + 1;
  for (let index = 0; index < maxSteps && runner.result === 'running'; index += 1) runner.step();
  return runner.summary();
}
